<script setup lang="ts">
interface Props {
  loading?: boolean
  quantity: number
  disableInteraction?: Array<'increase' | 'decrease'>
  errorMessage: string
}

withDefaults(defineProps<Props>(), {
  loading: false,
  disableInteraction: () => [],
})

const emit = defineEmits(['increase', 'decrease', 'inputChange'])
const onIncrease = () => emit('increase')
const onDecrease = () => emit('decrease')
const onChangeInput = (event: Event) => emit('inputChange', (event.target as HTMLInputElement).value)
</script>

<template>
  <div>
    <div class="qty">
      <button
        :disabled="disableInteraction.includes('decrease') || loading"
        @click="onDecrease"
      >
        <IconBasic filename="minus" />
      </button>
      <div class="qty-value">
        <LoadingSpinner v-if="loading" />
        <input
          data-test="quantity-input"
          class="value"
          type="number"
          min="1"
          max="10000"
          :onChange="onChangeInput"
          :placeholder="quantity.toString()"
          :value="quantity.toString()"
        >
      </div>
      <button
        :disabled="loading || disableInteraction.includes('increase')"
        @click="onIncrease"
      >
        <IconBasic filename="plus" />
      </button>
    </div>
    <p class="error-text">
      {{ errorMessage }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

.qty {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  place-items: center;
  border: 1px solid var(--gray-border);
  height: 2.5rem;
  width: 12rem;
  overflow: hidden;
}

.qty-value {
  width: 100%;
  height: 100%;
  position: relative;
  font-size: 1.2rem;
  .value,
  .loader {
    position: absolute;
    top: 50%;
    left: 50%;
  }
  .value {
    transform: translate(-50%, -50%);
    width:4em;
    text-align: center;
    -moz-appearance: textfield;
  }
  .value::-webkit-inner-spin-button,
  .value::-webkit-outer-spin-button {
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
  }
  .loader {
    width: 1.88rem !important;
    height: 1.88rem !important;
    z-index: 1;
    animation: fade-in 0.5s 1;
    background: rgba(255, 255, 255, 0.7);
    transform: translate(-50%, -48%);
  }
}

button {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease-in-out 0.2s;

  span.icon {
    width: 1rem;
    height: 1rem;
  }

  &:first-of-type {
    border-right: 1px solid var(--gray-border);
  }

  &:last-of-type {
    border-left: 1px solid var(--gray-border);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@media (min-width: $desktop) {
  .qty button:hover:not(:disabled) {
    color: var(--white);
    background: var(--black);
  }
}

.error-text {
  color: var(--red);
}
</style>
